/**
 * Inbox Icons Utility
 *
 * Maps Chatwoot channel types and mediums to Material-UI icons.
 * Provides a consistent icon representation for different communication channels.
 *
 * @see CONVERSATION_LIST_ICONS_GUIDE.md
 */

import React from 'react';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ChatIcon from '@mui/icons-material/Chat';
import PhoneIcon from '@mui/icons-material/Phone';
import CloudIcon from '@mui/icons-material/Cloud';
import SmsIcon from '@mui/icons-material/Sms';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { ChannelType } from '@/services/frontdesk/api/chatwoot/types/inboxes.types';
import type { ChatwootInbox } from '@/services/frontdesk/api/chatwoot/types/inboxes.types';
import { OutlookIcon } from '@/icons/outlook-icon';
import { GmailIcon } from '@/icons/gmail-icon';
import { WhatsAppBrandIcon } from '@/icons/whatsapp-icon';

// ============================================================================
// Type Definitions
// ============================================================================

export type InboxIconVariant = 'fill' | 'outline';
export type IconComponent = React.ComponentType<SvgIconProps>;

export interface InboxIconProps {
  channelType: string | null | undefined;
  medium?: string | null;
  /** Email provider brand (gmail / outlook / whatsapp) for brand logos */
  provider?: string | null;
  size?: number;
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_ICON_SIZE = 12; // size-3 equivalent (12px)

/**
 * Channel type to icon mapping
 * Maps Chatwoot channel types to their corresponding Material-UI icons
 */
const CHANNEL_ICON_MAP: Record<string, IconComponent> = {
  [ChannelType.EMAIL]: EmailIcon,
  [ChannelType.WHATSAPP]: WhatsAppIcon,
  [ChannelType.WEB_WIDGET]: ChatIcon,
  [ChannelType.FACEBOOK_PAGE]: ChatIcon,
  [ChannelType.TWITTER]: ChatIcon,
  [ChannelType.TELEGRAM]: ChatIcon,
  [ChannelType.LINE]: ChatIcon,
  [ChannelType.API]: CloudIcon,
  [ChannelType.SMS]: SmsIcon,
  // Legacy/alternative channel type names
  'Channel::Whatsapp': WhatsAppIcon,
  'Channel::TwitterProfile': ChatIcon,
  'Channel::Instagram': ChatIcon,
  'Channel::Voice': PhoneIcon,
  'Channel::Internal': ChatIcon,
} as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Determines the appropriate icon for Twilio SMS channels based on medium
 */
function getTwilioSmsIcon(medium?: string | null): IconComponent {
  return medium === 'whatsapp' ? WhatsAppIcon : SmsIcon;
}

/**
 * Gets the icon component for a given channel type, with fallback to default
 */
function getChannelIcon(channelType: string): IconComponent {
  return CHANNEL_ICON_MAP[channelType] || ChatIcon;
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Gets the Material-UI icon component for a given channel type and medium
 *
 * @param channelType - The channel type (e.g., "Channel::Email")
 * @param medium - Optional medium (e.g., "whatsapp" for TwilioSMS)
 * @param _variant - Icon variant (maintained for API compatibility, not used with MUI)
 * @returns React component for the icon
 *
 * @example
 * ```tsx
 * const EmailIconComponent = getInboxIconByType('Channel::Email');
 * const WhatsAppIconComponent = getInboxIconByType('Channel::TwilioSms', 'whatsapp');
 * const DefaultIconComponent = getInboxIconByType(null); // Returns ChatIcon
 * ```
 */
export function getInboxIconByType(
  channelType: string | null | undefined,
  medium?: string | null,
  _variant: InboxIconVariant = 'fill' // Kept for API compatibility
): IconComponent {
  if (!channelType) {
    return ChatIcon;
  }

  // Special handling for Twilio SMS - check medium first
  if (channelType === ChannelType.TWILIO_SMS) {
    return getTwilioSmsIcon(medium);
  }

  return getChannelIcon(channelType);
}

// ============================================================================
// Channel Indicator (for avatar badge)
// ============================================================================

export interface ChannelIndicator {
  /** Small icon (10px) for the badge overlay */
  icon: React.ReactNode;
  /** Larger icon (18px) for use as avatar fallback when no customer photo */
  avatarIcon: React.ReactNode;
  color: string;
  /** Stable brand key for labels / future features */
  brand: 'gmail' | 'outlook' | 'whatsapp' | 'email' | 'other';
}

/**
 * Gets a channel indicator (icon + brand color) for a given inbox.
 * Used to render a small badge on the ticket card avatar.
 *
 * - WhatsApp → WhatsApp icon, green (#25D366)
 * - Gmail → Google icon, red (#EA4335)
 * - Outlook → Outlook icon, blue (#0078D4)
 * - Other email → Email icon, neutral
 */
export function getChannelIndicator(inbox: ChatwootInbox | null | undefined): ChannelIndicator | null {
  if (!inbox) return null;

  const channelType = inbox.channel_type;

  // WhatsApp channels (channel_type and/or provider)
  if (
    channelType === ChannelType.WHATSAPP ||
    channelType === 'Channel::Whatsapp' ||
    channelType === 'Channel::WhatsApp' ||
    /whatsapp/i.test(String(channelType || '')) ||
    String(inbox.provider || '').toLowerCase() === 'whatsapp' ||
    (channelType === ChannelType.TWILIO_SMS && inbox.medium === 'whatsapp')
  ) {
    return {
      icon: <WhatsAppBrandIcon size={14} />,
      avatarIcon: <WhatsAppBrandIcon size={22} />,
      color: '#25D366',
      brand: 'whatsapp',
    };
  }

  // Email channels — distinguish Gmail vs Outlook
  if (channelType === ChannelType.EMAIL || String(channelType || '').includes('Email')) {
    const provider = String(
      inbox.provider || inbox._emailAccount?.provider || '',
    ).toLowerCase();
    const nameLower = (inbox.name || '').toLowerCase();
    const emailLower = (
      inbox.email ||
      inbox.sender_email ||
      inbox.forward_to_email ||
      inbox._emailAccount?.email ||
      ''
    ).toLowerCase();
    // Check IMAP/SMTP server addresses — these identify the provider
    // regardless of user's email domain (e.g. mike@company.com on Gmail uses imap.gmail.com)
    const serverFields = [
      inbox.imap_address,
      inbox.smtp_address,
    ].filter(Boolean).join(' ').toLowerCase();

    const isGmail =
      provider === 'gmail' ||
      provider === 'google' ||
      nameLower.includes('gmail') ||
      nameLower.includes('google') ||
      emailLower.includes('@gmail.com') ||
      emailLower.includes('@googlemail.com') ||
      serverFields.includes('gmail.com') ||
      serverFields.includes('google.com');

    const isOutlook =
      provider === 'outlook' ||
      provider === 'microsoft' ||
      nameLower.includes('outlook') ||
      nameLower.includes('microsoft') ||
      emailLower.includes('@outlook.') ||
      emailLower.includes('@hotmail.') ||
      emailLower.includes('@live.') ||
      emailLower.includes('@msn.') ||
      emailLower.includes('@office365.') ||
      serverFields.includes('outlook.office365.com') ||
      serverFields.includes('outlook.com') ||
      serverFields.includes('office365.com');

    if (isGmail) {
      return {
        icon: <GmailIcon size={14} />,
        avatarIcon: <GmailIcon size={22} />,
        color: '#EA4335',
        brand: 'gmail',
      };
    }

    if (isOutlook) {
      return {
        icon: <OutlookIcon size={14} />,
        avatarIcon: <OutlookIcon size={22} />,
        color: '#0078D4',
        brand: 'outlook',
      };
    }

    // Generic / unrecognized email provider
    return {
      icon: <EmailIcon sx={{ fontSize: 14, color: '#5C6BC0' }} />,
      avatarIcon: <EmailIcon sx={{ fontSize: 20, color: '#5C6BC0' }} />,
      color: '#5C6BC0',
      brand: 'email',
    };
  }

  return null;
}

/**
 * Human-readable channel label for inbox cards (Gmail / Outlook / WhatsApp / Email).
 */
export function getChannelDisplayName(
  inbox: ChatwootInbox | null | undefined,
  fallbackChannel?: string | null,
): string {
  const indicator = getChannelIndicator(inbox);
  if (indicator?.brand === 'whatsapp') return 'WhatsApp';
  if (indicator?.brand === 'gmail') return 'Gmail';
  if (indicator?.brand === 'outlook') return 'Outlook';
  if (indicator?.brand === 'email') return 'Email';

  const raw = inbox?.channel_type || fallbackChannel || '';
  const cleaned = String(raw).replace(/^Channel::/i, '').trim();
  if (!cleaned) return 'Unknown channel';
  if (/whatsapp/i.test(cleaned)) return 'WhatsApp';
  if (/email/i.test(cleaned)) return 'Email';
  return cleaned;
}

// ============================================================================
// Component
// ============================================================================

/**
 * InboxIcon Component
 *
 * Renders a channel-specific icon with configurable size.
 * Uses Gmail / Outlook / WhatsApp brand logos when `provider` is set.
 *
 * @example
 * ```tsx
 * <InboxIcon channelType="Channel::Email" size={16} />
 * <InboxIcon channelType="Channel::Email" provider="gmail" size={12} />
 * <InboxIcon channelType="Channel::TwilioSms" medium="whatsapp" size={12} />
 * ```
 */
export const InboxIcon: React.FC<InboxIconProps> = ({
  channelType,
  medium,
  provider,
  size = DEFAULT_ICON_SIZE,
  className,
}) => {
  const brand = String(provider || medium || "")
    .trim()
    .toLowerCase();

  if (brand === "gmail" || brand === "google") {
    return (
      <span className={className} style={{ display: "inline-flex", lineHeight: 0 }}>
        <GmailIcon size={size} />
      </span>
    );
  }

  if (brand === "outlook" || brand === "microsoft") {
    return (
      <span className={className} style={{ display: "inline-flex", lineHeight: 0 }}>
        <OutlookIcon size={size} />
      </span>
    );
  }

  if (brand === "whatsapp") {
    return (
      <span className={className} style={{ display: "inline-flex", lineHeight: 0 }}>
        <WhatsAppBrandIcon size={size} />
      </span>
    );
  }

  const IconComponent = getInboxIconByType(channelType, medium);

  return (
    <IconComponent
      sx={{
        fontSize: size,
        color: "inherit",
      }}
      className={className}
    />
  );
};
